import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import PromoBanner from "@/components/site/PromoBanner";
import SEO from "@/components/seo/SEO";
import AboutSection from "@/components/site/About";

const PRINCIPLES = [
  {
    n: "01",
    title: "Useful on day one.",
    body: "Every kit is designed to give you a usable result the first time you open it. No multi-week onboarding, no learning curve.",
  },
  {
    n: "02",
    title: "Calm by default.",
    body: "We don't believe in maximalist productivity. Our tools are small, opinionated, and quiet — built to fit into your day, not run it.",
  },
  {
    n: "03",
    title: "Honest about outcomes.",
    body: "We make tools, not promises. You won't see '10x your output' anywhere — but you'll write better emails by tonight.",
  },
  {
    n: "04",
    title: "Pay your way.",
    body: "Buy any kit on its own, or unlock the entire library with a Studio membership. Cancel anytime — your access continues to the end of the period.",
  },
];

const About = () => {
  return (
    <main
      data-testid="about-page"
      className="min-h-screen bg-[#F7F7F4] flex flex-col"
    >
      <SEO
        title="About"
        description="Vaymilo is a small independent digital product studio. We make practical AI kits, templates, and workflows for everyday work."
        path="/about"
      />
      <PromoBanner />
      <Header />

      {/* Page hero */}
      <section className="bg-[#F7F7F4] border-b border-[#E7E7E2]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
          <div className="overline">— About</div>
          <h1 className="mt-5 font-sora text-[44px] sm:text-[60px] lg:text-[80px] font-extrabold tracking-tight leading-[1.02] text-[#111111]">
            A small studio
            <br className="hidden sm:block" /> for simple systems.
          </h1>
          <p className="mt-6 max-w-2xl font-manrope text-[17px] md:text-[19px] leading-[1.65] text-[#111111]/70">
            Vaymilo makes <strong className="text-[#111111]">finished digital products</strong> —
            kits, templates, and workflows you can use today. Available
            individually or all-access via Studio membership. We do not offer
            custom design, development, consulting, or freelance services.
          </p>
          <div
            data-testid="about-how-we-sell"
            className="mt-7 inline-flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[10px] tracking-[0.14em] uppercase text-[#111111]/65"
          >
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-[#2563EB]" /> Instant digital downloads
            </span>
            <span className="text-[#111111]/25">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-[#2563EB]" /> Individual or all-access
            </span>
            <span className="text-[#111111]/25">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-[#2563EB]" /> Cancel anytime
            </span>
          </div>
        </div>
      </section>

      <AboutSection />

      {/* Principles */}
      <section className="bg-white border-y border-[#E7E7E2]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="overline">— Principles</div>
          <h2 className="mt-5 font-sora text-[36px] sm:text-[44px] lg:text-[52px] font-bold text-[#111111] tracking-tight leading-[1.05]">
            How we build.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E7E7E2] border border-[#E7E7E2] rounded-2xl overflow-hidden">
            {PRINCIPLES.map((p) => (
              <div
                key={p.n}
                data-testid={`principle-${p.n}`}
                className="bg-white p-8 md:p-10 min-h-[200px]"
              >
                <div className="font-mono text-xs text-[#111111]/45">{p.n}</div>
                <h3 className="mt-6 font-sora text-[22px] md:text-[24px] font-semibold text-[#111111] tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-3 font-manrope text-[15px] md:text-[16px] text-[#111111]/65 leading-relaxed max-w-md">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we make / don't make */}
      <section className="bg-[#F7F7F4]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="overline">— Focus</div>
              <h2 className="mt-5 font-sora text-[32px] md:text-[42px] font-bold text-[#111111] tracking-tight leading-[1.05]">
                What we make.
                <br />
                What we don&rsquo;t.
              </h2>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E7E7E2] border border-[#E7E7E2] rounded-2xl overflow-hidden">
              <div className="bg-white p-7 md:p-8">
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#2563EB]">
                  We make
                </div>
                <ul className="mt-4 space-y-3 font-manrope text-[15px] text-[#111111]/85">
                  <li>Small, opinionated digital kits</li>
                  <li>Clean templates that work today</li>
                  <li>Workflows you actually finish</li>
                  <li>Prompts for everyday writing</li>
                  <li>Quiet, useful product updates</li>
                </ul>
              </div>
              <div className="bg-white p-7 md:p-8">
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#111111]/55">
                  We don&rsquo;t make
                </div>
                <ul className="mt-4 space-y-3 font-manrope text-[15px] text-[#111111]/70">
                  <li>Custom design, development, or consulting work</li>
                  <li>Freelance, agency, or done-for-you services</li>
                  <li>Hype courses or &ldquo;mindset&rdquo; programs</li>
                  <li>SaaS dashboards or AI chat wrappers</li>
                  <li>Promises about money, growth, or productivity</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-white border-t border-[#E7E7E2]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="overline">— Say hello</div>
              <h2 className="mt-4 font-sora text-[32px] md:text-[44px] font-bold text-[#111111] tracking-tight leading-[1.05]">
                Working on something? Tell us about it.
              </h2>
              <p className="mt-5 max-w-xl font-manrope text-[16px] md:text-[17px] text-[#111111]/65">
                We answer every message from a real inbox — usually within
                one or two business days.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/contact"
                data-testid="about-contact-link"
                className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-[15px] px-6 py-3.5 rounded-lg transition-colors"
              >
                Send a message
                <ArrowUpRight size={16} strokeWidth={2.25} />
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 bg-white text-[#111111] font-manrope font-medium text-[15px] px-6 py-3.5 rounded-lg border border-[#E7E7E2] hover:border-[#111111] transition-colors"
              >
                Browse products
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default About;
